import { z } from 'zod';
import {
    PillarPaymentRequestType,
    type PillarProcessCardTokenPaymentRequest,
    type PillarTerminalSaleRequest,
} from '../../../payment.types';
import {
    CardPointeSidebarPaymentEntryActionType,
    CardPointeSidebarPaymentEntryOutcome,
    type CardPointeSidebarPaymentEntryAction,
    type CardPointeSidebarPaymentEntryNormalizedResult,
    type CardPointeSidebarPaymentEntryRequest,
    type CardPointeSidebarPaymentEntryRequestDetails,
    type CardPointeSidebarPaymentEntryResponseDetails,
    type CardPointeSupportedSidebarPaymentEntryAction,
    type CardPointeUnsupportedSidebarPaymentEntryAction,
} from '../../../types/ipg/cardpointe/cardpointe-sidebar-payment-entry.types';
import {
    ProcessCardTokenPaymentRequestSchema,
    TerminalSaleRequestSchema,
    TransactionDetailsSchema,
} from '../../payment.schemas';

export const CardPointeTerminalSaleSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryActionType.TERMINAL_SALE>;
    request: typeof TerminalSaleRequestSchema;
}> = z
    .object({
        actionType: z.literal(CardPointeSidebarPaymentEntryActionType.TERMINAL_SALE),
        request: TerminalSaleRequestSchema,
    })
    .strict();

export const CardPointeTokenSaleSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryActionType.TOKEN_SALE>;
    request: typeof ProcessCardTokenPaymentRequestSchema;
}> = z
    .object({
        actionType: z.literal(CardPointeSidebarPaymentEntryActionType.TOKEN_SALE),
        request: ProcessCardTokenPaymentRequestSchema,
    })
    .strict();

export const CardPointeStoredTokenSaleSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryActionType.STORED_TOKEN_SALE>;
    request: typeof ProcessCardTokenPaymentRequestSchema;
}> = z
    .object({
        actionType: z.literal(CardPointeSidebarPaymentEntryActionType.STORED_TOKEN_SALE),
        request: ProcessCardTokenPaymentRequestSchema,
    })
    .strict();

export const CardPointeUnsupportedSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryActionType.ACH_PAYMENT>;
}> = z
    .object({
        actionType: z.literal(CardPointeSidebarPaymentEntryActionType.ACH_PAYMENT),
    })
    .strict() satisfies z.ZodType<CardPointeUnsupportedSidebarPaymentEntryAction>;

export const CardPointeSupportedSidebarPaymentEntryActionSchema: z.ZodType<CardPointeSupportedSidebarPaymentEntryAction> =
    z.discriminatedUnion('actionType', [
        CardPointeTerminalSaleSidebarPaymentEntryActionSchema,
        CardPointeTokenSaleSidebarPaymentEntryActionSchema,
        CardPointeStoredTokenSaleSidebarPaymentEntryActionSchema,
    ]);

export const CardPointeSidebarPaymentEntryActionSchema: z.ZodType<CardPointeSidebarPaymentEntryAction> =
    z.discriminatedUnion('actionType', [
        CardPointeTerminalSaleSidebarPaymentEntryActionSchema,
        CardPointeTokenSaleSidebarPaymentEntryActionSchema,
        CardPointeStoredTokenSaleSidebarPaymentEntryActionSchema,
        CardPointeUnsupportedSidebarPaymentEntryActionSchema,
    ]);

export const CardPointeSidebarPaymentEntryRequestDetailsSchema: z.ZodObject<{
    action: typeof CardPointeSidebarPaymentEntryActionSchema;
}> = z
    .object({
        action: CardPointeSidebarPaymentEntryActionSchema,
    })
    .strict() satisfies z.ZodType<CardPointeSidebarPaymentEntryRequestDetails>;

export const CardPointeSidebarPaymentEntryTerminalSaleRequestSchema: z.ZodObject<{
    request_type: z.ZodLiteral<typeof PillarPaymentRequestType.TERMINAL_SALE>;
    request: typeof TerminalSaleRequestSchema;
    transaction_details: typeof TransactionDetailsSchema;
}> = z
    .object({
        request_type: z.literal(PillarPaymentRequestType.TERMINAL_SALE),
        request: TerminalSaleRequestSchema,
        transaction_details: TransactionDetailsSchema,
    })
    .strict() satisfies z.ZodType<PillarTerminalSaleRequest>;

export const CardPointeSidebarPaymentEntryTokenSaleRequestSchema: z.ZodObject<{
    request_type: z.ZodLiteral<typeof PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT>;
    request: typeof ProcessCardTokenPaymentRequestSchema;
    transaction_details: typeof TransactionDetailsSchema;
}> = z
    .object({
        request_type: z.literal(PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT),
        request: ProcessCardTokenPaymentRequestSchema,
        transaction_details: TransactionDetailsSchema,
    })
    .strict() satisfies z.ZodType<PillarProcessCardTokenPaymentRequest>;

export const CardPointeSidebarPaymentEntryRequestSchema: z.ZodType<CardPointeSidebarPaymentEntryRequest> =
    z.discriminatedUnion('request_type', [
        CardPointeSidebarPaymentEntryTerminalSaleRequestSchema,
        CardPointeSidebarPaymentEntryTokenSaleRequestSchema,
    ]);

export const CardPointeSidebarPaymentEntryNormalizedResultSchema: z.ZodType<CardPointeSidebarPaymentEntryNormalizedResult> =
    z.object({
        actionType: z.enum([
            CardPointeSidebarPaymentEntryActionType.TERMINAL_SALE,
            CardPointeSidebarPaymentEntryActionType.TOKEN_SALE,
            CardPointeSidebarPaymentEntryActionType.STORED_TOKEN_SALE,
        ]),
        requestType: z.enum([
            PillarPaymentRequestType.TERMINAL_SALE,
            PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT,
        ]),
        transactionDetails: TransactionDetailsSchema,
    });

export const CardPointeSidebarPaymentEntryBuiltResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryOutcome.BUILT>;
    paymentRequest: typeof CardPointeSidebarPaymentEntryRequestSchema;
    normalizedResult: typeof CardPointeSidebarPaymentEntryNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CardPointeSidebarPaymentEntryOutcome.BUILT),
    paymentRequest: CardPointeSidebarPaymentEntryRequestSchema,
    normalizedResult: CardPointeSidebarPaymentEntryNormalizedResultSchema,
});

export const CardPointeSidebarPaymentEntryUnsupportedActionResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryOutcome.UNSUPPORTED_ACTION>;
    actionType: z.ZodLiteral<typeof CardPointeSidebarPaymentEntryActionType.ACH_PAYMENT>;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeSidebarPaymentEntryOutcome.UNSUPPORTED_ACTION),
    actionType: z.literal(CardPointeSidebarPaymentEntryActionType.ACH_PAYMENT),
    message: z.string().min(1),
});

export const CardPointeSidebarPaymentEntryResponseDetailsSchema: z.ZodType<CardPointeSidebarPaymentEntryResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeSidebarPaymentEntryBuiltResponseDetailsSchema,
        CardPointeSidebarPaymentEntryUnsupportedActionResponseDetailsSchema,
    ]);
